import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../lib/adminAPI';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import AdminUserTrades from './AdminUserTrades';
import { Search, Edit2, Trash2, Shield, X, Save, Ban, Bell, Send, LogIn, Activity, Download } from 'lucide-react';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [notifying, setNotifying] = useState(null);
  const [msg, setMsg] = useState({ title: '', message: '', type: 'info' });
  const [managing, setManaging] = useState(null);
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { impersonate } = useAuth();

  const load = async () => {
    setLoading(true);
    try { setUsers(await adminAPI.users(q)); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openEdit = (u) => { setEditing(u.id); setForm({ name: u.name, email: u.email, balance: u.balance, role: u.role || 'user', banned: u.banned || false, leverage: u.leverage ?? 1, volatility_factor: u.volatility_factor ?? 1 }); };

  const save = async () => {
    try {
      await adminAPI.updateUser(editing, { name: form.name, balance: parseFloat(form.balance), role: form.role, banned: form.banned });
      await adminAPI.setTrading(editing, { leverage: parseFloat(form.leverage) || 1, volatility_factor: parseFloat(form.volatility_factor) || 1 });
      toast({ title: 'User updated' });
      setEditing(null);
      load();
    } catch (e) { toast({ title: 'Update failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  const doImpersonate = async (u) => {
    try {
      const { token, user } = await adminAPI.impersonate(u.id);
      impersonate(token, user);
      toast({ title: `Now viewing as ${user.name}` });
      navigate('/dashboard');
    } catch (e) { toast({ title: 'Could not impersonate', description: e?.response?.data?.detail || 'Try again' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this user and all their data?')) return;
    try {
      await adminAPI.deleteUser(id);
      toast({ title: 'User deleted' });
      load();
    } catch (e) { toast({ title: 'Delete failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  const openNotify = (u) => { setNotifying(u); setMsg({ title: '', message: '', type: 'info' }); };

  const exportAll = async (fmt) => {
    setExporting(true);
    try {
      const res = await api.get('/admin/users/export', { params: { fmt }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_backup_${new Date().toISOString().replace(/[:.]/g, '-')}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast({ title: `Exported ${users.length || 'all'} users (${fmt.toUpperCase()})` });
    } catch (e) {
      toast({ title: 'Export failed', description: e?.response?.data?.detail || 'Try again' });
    }
    setExporting(false);
  };
  const sendNotify = async () => {
    if (!msg.title.trim() || !msg.message.trim()) { toast({ title: 'Title and message required' }); return; }
    try {
      await adminAPI.notifyUser(notifying.id, msg);
      toast({ title: 'Message sent', description: `Delivered to ${notifying.email}` });
      setNotifying(null);
    } catch (e) { toast({ title: 'Failed to send', description: e?.response?.data?.detail || 'Try again' }); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="text-sm text-gray-400">Manage all trader accounts.</p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative w-72">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by email or name…" className="w-full bg-[#0a1210] border border-[#1a2823] rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]"/>
        </form>
        <div className="flex items-center gap-2">
          <button onClick={() => exportAll('csv')} disabled={exporting} data-testid="export-users-csv"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#00e676]/40 bg-[#00e676]/10 text-[#00e676] text-xs font-semibold hover:bg-[#00e676]/20 disabled:opacity-50">
            <Download size={13}/> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button onClick={() => exportAll('json')} disabled={exporting} data-testid="export-users-json"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#1a2823] bg-[#0a1210] text-gray-300 text-xs font-semibold hover:border-[#00e676]/30 hover:text-white disabled:opacity-50">
            <Download size={13}/> JSON
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3">User</th><th>Balance</th><th>Role</th><th>KYC</th><th>Status</th><th>Joined</th><th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="7" className="py-6 text-center text-sm text-gray-500">Loading…</td></tr>}
              {!loading && users.length === 0 && <tr><td colSpan="7" className="py-10 text-center text-sm text-gray-500">No users found</td></tr>}
              {users.map(u => (
                <tr key={u.id} className="border-t border-[#141f1b] hover:bg-[#0f1a17]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#00e676]/15 border border-[#00e676]/30 flex items-center justify-center text-[#00e676] text-xs font-bold">{u.name?.[0]?.toUpperCase() || 'U'}</div>
                      <div>
                        <div className="text-sm text-white font-semibold">{u.name}</div>
                        <div className="text-[10px] text-gray-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="mono text-white">${(u.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>
                    {u.role === 'admin' ? <span className="px-2 py-0.5 rounded bg-purple-500/15 text-purple-400 text-[10px] font-bold"><Shield size={9} className="inline mr-1"/>ADMIN</span> : <span className="text-[10px] text-gray-400">USER</span>}
                  </td>
                  <td>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${u.kyc_status === 'approved' ? 'bg-[#00e676]/15 text-[#00e676]' : u.kyc_status === 'pending' ? 'bg-amber-500/15 text-amber-400' : u.kyc_status === 'rejected' ? 'bg-red-500/15 text-red-400' : 'bg-[#1a2823] text-gray-500'}`}>
                      {(u.kyc_status || 'not started').toUpperCase()}
                    </span>
                  </td>
                  <td>
                    {u.banned ? <span className="text-[10px] text-red-400 font-semibold"><Ban size={10} className="inline mr-1"/>BANNED</span> : <span className="text-[10px] text-[#00e676]">Active</span>}
                  </td>
                  <td className="text-xs text-gray-400 mono">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                  <td className="text-right pr-4">
                    <button onClick={() => doImpersonate(u)} data-testid={`impersonate-${u.email}`} title="Log in as user" className="p-1.5 mr-1 rounded border border-[#1a2823] text-gray-400 hover:text-blue-400 hover:border-blue-500/40"><LogIn size={12}/></button>
                    <button onClick={() => setManaging(u)} data-testid={`manage-trades-${u.email}`} title="Manage live trades" className="p-1.5 mr-1 rounded border border-[#1a2823] text-gray-400 hover:text-purple-400 hover:border-purple-500/40"><Activity size={12}/></button>
                    <button onClick={() => openNotify(u)} data-testid={`notify-user-${u.email}`} title="Send message" className="p-1.5 mr-1 rounded border border-[#1a2823] text-gray-400 hover:text-[#00e676] hover:border-[#00e676]/40"><Bell size={12}/></button>
                    <button onClick={() => openEdit(u)} className="p-1.5 mr-1 rounded border border-[#1a2823] text-gray-400 hover:text-[#00e676] hover:border-[#00e676]/40"><Edit2 size={12}/></button>
                    <button onClick={() => remove(u.id)} className="p-1.5 rounded border border-[#1a2823] text-gray-400 hover:text-red-400 hover:border-red-500/40"><Trash2 size={12}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold">Edit User</h3>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-white"><X size={16}/></button>
            </div>
            <div className="space-y-3">
              <div><label className="text-[10px] text-gray-400 uppercase">Email</label><div data-testid="edit-user-email" className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-sm text-gray-400 truncate">{form.email || '—'}</div></div>
              <div><label className="text-[10px] text-gray-400 uppercase">Name</label><input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]"/></div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-[10px] text-gray-400 uppercase">Leverage</label>
                  <select value={String(parseFloat(form.leverage) || 1)} onChange={e => setForm({ ...form, leverage: e.target.value })} data-testid="edit-user-leverage" className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]">
                    <option value="1">1:1 (None)</option>
                    <option value="50">1:50</option>
                    <option value="100">1:100</option>
                    <option value="200">1:200</option>
                    <option value="500">1:500</option>
                    <option value="1000">1:1000</option>
                    <option value="2000">1:2000</option>
                    <option value="5000">1:Unlimited</option>
                  </select>
                </div>
                <div><label className="text-[10px] text-gray-400 uppercase">Volatility (x)</label><input type="number" min="0.1" step="0.1" value={form.volatility_factor ?? 1} onChange={e => setForm({ ...form, volatility_factor: e.target.value })} data-testid="edit-user-volatility" className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]"/></div>
              </div>
              <div><label className="text-[10px] text-gray-400 uppercase">Balance ($)</label><input type="number" step="0.01" value={form.balance || 0} onChange={e => setForm({ ...form, balance: e.target.value })} className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-sm text-white mono focus:outline-none focus:border-[#00e676]"/></div>
              <div><label className="text-[10px] text-gray-400 uppercase">Role</label>
                <select value={form.role || 'user'} onChange={e => setForm({ ...form, role: e.target.value })} className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]">
                  <option value="user">User</option><option value="admin">Admin</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={form.banned || false} onChange={e => setForm({ ...form, banned: e.target.checked })} className="accent-red-500"/>
                Ban this user
              </label>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded border border-[#1a2823] text-gray-300 text-sm">Cancel</button>
              <button onClick={save} className="btn-green px-4 py-2 rounded text-sm font-semibold flex items-center gap-1.5"><Save size={13}/> Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {managing && <AdminUserTrades user={managing} onClose={() => setManaging(null)} />}

      {notifying && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]" data-testid="notify-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-white font-bold flex items-center gap-2"><Bell size={15} className="text-[#00e676]"/> Send Message</h3>
              <button onClick={() => setNotifying(null)} className="text-gray-400 hover:text-white"><X size={16}/></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">To {notifying.name} · {notifying.email}</p>
            <div className="space-y-3">
              <div><label className="text-[10px] text-gray-400 uppercase">Type</label>
                <select value={msg.type} onChange={e => setMsg({ ...msg, type: e.target.value })} data-testid="notify-type" className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]">
                  <option value="info">Info</option><option value="success">Success</option><option value="warning">Warning</option><option value="alert">Alert</option>
                </select>
              </div>
              <div><label className="text-[10px] text-gray-400 uppercase">Title</label><input value={msg.title} onChange={e => setMsg({ ...msg, title: e.target.value })} data-testid="notify-title" maxLength={140} placeholder="e.g. Account Update" className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]"/></div>
              <div><label className="text-[10px] text-gray-400 uppercase">Message</label><textarea value={msg.message} onChange={e => setMsg({ ...msg, message: e.target.value })} data-testid="notify-message" rows={4} maxLength={2000} placeholder="Type any message to send directly to this user…" className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676] resize-none"/></div>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => setNotifying(null)} className="px-4 py-2 rounded border border-[#1a2823] text-gray-300 text-sm">Cancel</button>
              <button onClick={sendNotify} data-testid="notify-send-btn" className="btn-green px-4 py-2 rounded text-sm font-semibold flex items-center gap-1.5"><Send size={13}/> Send Message</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
